import React, { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../pack-icons';
import { formatMetric, metricNumber, metricSegments, nullableSum } from '../../lib/socialMetrics.js';
import { smoothMetricPath } from '../../lib/chartPaths.js';
import { MetricSeriesTable } from '../metrics/MetricValue.jsx';

/**
 * EngagementChart — interactive SVG chart of engagement over time.
 *
 * data: [{ date, label, views, engagement, published }]  (chronological)
 * valueMode: 'rate' (engagement = %, default) | 'count' (engagement = absolute
 *   activity count e.g. likes+comments+shares+saves). Count mode is used when
 *   the user's connected platforms haven't returned reach/impression data yet
 *   (typical for IG/Threads with pending `*_manage_insights` approval). It
 *   determines the engagement unit when that metric is selected.
 * metric: 'views' (default in the dashboard) | 'engagement'
 * partial: true → explains missing data only in the compact details disclosure
 * onRangeChange: optional callback when user clicks a range button
 */
export default function EngagementChart({ data = [], height = 220, partial = false, onRangeChange, valueMode = 'rate', metric = 'engagement', embedded = false, rangeDays = 30, summaryValue = null, reportedViews = null, viewsMeasuredTargets = 0, publishedTargets = null, measuredTargets = null, hasCoverage = false }) {
  const { t, i18n } = useTranslation();
  const isCountMode = valueMode === 'count';
  const selectedMetric = metric === 'views' ? 'views' : 'engagement';
  const valueUnit = selectedMetric === 'engagement' && !isCountMode ? '%' : '';
  const seriesLabel = selectedMetric === 'views' ? t('socialMetrics.views') : isCountMode ? t('engagementChart.engagement') : t('engagementChart.engagementPct');
  const formatValue = (v) => {
    if (!Number.isFinite(v)) return '—';
    if (selectedMetric === 'views' || isCountMode) return v.toLocaleString(i18n.resolvedLanguage || i18n.language || 'en');
    return v.toFixed(1);
  };
  const [range, setRange] = useState('30d');
  const [hover, setHover] = useState(null);
  const [tooltipLeft, setTooltipLeft] = useState(0);
  const svgRef = useRef(null);
  const plotRef = useRef(null);

  const slice = useMemo(() => {
    const map = { '7d': 7, '14d': 14, '30d': 30, '90d': 90 };
    const n = embedded ? rangeDays : (map[range] || 30);
    return data.slice(-n);
  }, [data, range, embedded, rangeDays]);

  if (!data?.length) {
    if (embedded) {
      return (
        <div className="an-empty">
          <I.info size={22} />
          <div className="an-empty-t">{t('engagementChart.noAnalyticsYet')}</div>
          <div className="an-empty-h">{t('engagementChart.engagementAppearsHint')}</div>
        </div>
      );
    }
    return (
      <div className="chart-card card">
        <div className="chart-head">
          <div>
            <div className="eyebrow"><span>{t('engagementChart.engagement')}</span></div>
            <h3 className="chart-title">{t('engagementChart.noAnalyticsYet')}</h3>
            <div className="chart-partial">
              <I.info size={12} />
              <span>{t('engagementChart.analyticsDataAppearsHint')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const W = 760;
  const H = height;
  const padX = 38;
  const padY = 22;
  const innerW = W - padX * 2;
  const innerH = H - padY * 2;

  // The selected metric and publication counts have separate, labeled axes.
  const maxYRaw = Math.max(0, ...slice.map((d) => metricNumber(d[selectedMetric]) ?? 0));
  const maxPosts = Math.max(1, Math.ceil(Math.max(0, ...slice.map(d => metricNumber(d.published) ?? 0)) * 1.2));
  const maxY = selectedMetric === 'engagement' && !isCountMode
    ? (maxYRaw > 0 ? maxYRaw * 1.2 : 5)
    : Math.max(5, Math.ceil(maxYRaw * 1.2));
  const minY = 0;
  const xStep = innerW / Math.max(1, slice.length - 1);

  const xy = (i, v) => [padX + i * xStep, padY + innerH - ((v - minY) / (maxY - minY)) * innerH];
  const xyPosts = (i, v) => [padX + i * xStep, padY + innerH - (v / maxPosts) * innerH];

  const buildPath = (key) => {
    return metricSegments(slice, key, xy).map(smoothMetricPath).join(' ');
  };

  const selectedPath = buildPath(selectedMetric);
  const publishedPath = metricSegments(slice, 'published', xyPosts).map(smoothMetricPath).join(' ');
  const areaPath = metricSegments(slice, selectedMetric, xy).filter(points => points.length > 1).map(points =>
    smoothMetricPath(points)
      + ` L ${points[points.length - 1][0]} ${padY + innerH} L ${points[0][0]} ${padY + innerH} Z`
  ).join(' ');
  const observed = slice.flatMap((row, index) => metricNumber(row[selectedMetric]) === null ? [] : [index]);
  const gapBridges = observed.slice(1).flatMap((index, i) => {
    const previous = observed[i];
    return index - previous > 1 ? [[xy(previous, slice[previous][selectedMetric]), xy(index, slice[index][selectedMetric])]] : [];
  });

  const onMove = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W - padX;
    const i = Math.max(0, Math.min(slice.length - 1, Math.round(x / xStep)));
    setHover(i);
    const plot = plotRef.current?.getBoundingClientRect();
    if (plot) setTooltipLeft(Math.max(0, Math.min(plot.width - Math.min(220, plot.width), e.clientX - plot.left - 110)));
  };
  const onLeave = () => setHover(null);

  const xLabels = slice.length > 14
    ? [0, Math.round((slice.length - 1) * 0.5), slice.length - 1]
    : slice.length > 7
      ? [0, Math.round((slice.length - 1) * 0.33), Math.round((slice.length - 1) * 0.66), slice.length - 1]
      : slice.map((_, i) => i);
  const yLabels = 4;

  const publicationDays = slice.filter(d => metricNumber(d.published) !== 0);
  const totalEngagement = nullableSum(publicationDays.map(d => d.engagement));
  const observedPublicationDays = publicationDays.filter(d => metricNumber(d.engagement) !== null).length;
  const knownPublished = slice.every(d => metricNumber(d.published) !== null) ? slice.reduce((s, d) => s + d.published, 0) : null;
  const totalPublished = metricNumber(publishedTargets) ?? knownPublished;
  // In rate mode the header reads avg %, in count mode it reads total
  // engagements (sum is the natural aggregation for counts; an avg would
  // be misleading because days without posts read 0).
  const headlineValue = metricNumber(summaryValue) !== null ? (isCountMode ? Math.round(summaryValue).toString() : summaryValue.toFixed(1))
    : totalEngagement === null ? '—' : isCountMode
    ? Math.round(totalEngagement).toString()
    : (observedPublicationDays > 0 ? (totalEngagement / observedPublicationDays).toFixed(1) : totalPublished === 0 ? '0.0' : '—');
  const viewsValue = viewsMeasuredTargets > 0 && metricNumber(reportedViews) !== null
    ? reportedViews.toLocaleString(i18n.resolvedLanguage || i18n.language || 'en')
    : totalPublished === 0 ? '0' : embedded ? '—' : (nullableSum(slice.map(d => d.views)) ?? '—');

  return (
    <div className={embedded ? 'chart-embedded' : 'chart-card card'}>
      {!embedded && (
      <div className="chart-head">
        <div>
          <div className="eyebrow"><span>{t('engagementChart.engagement')}</span></div>
          <h3 className="chart-title">{t('engagementChart.lastNDays', { count: slice.length })}</h3>
          {partial && (
            <div className="chart-partial">
              <I.info size={12} />
              <span>{t('engagementChart.partialDataHint')}</span>
            </div>
          )}
        </div>
        <div className="chart-ranges">
          {['7d', '14d', '30d', '90d'].map((r) => (
            <button
              key={r}
              className={r === range ? 'is-active' : ''}
              onClick={() => { setRange(r); onRangeChange?.(r); }}
              type="button"
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      )}
      <div className="chart-stats">
        <div>
          <div className="cs-l">{t('socialMetrics.views')}</div>
          <div className="cs-v">{viewsValue}</div>
        </div>
        <div>
          <div className="cs-l">{t('engagementChart.postsUnit')}</div>
          <div className="cs-v">
            {totalPublished ?? '—'}
          </div>
        </div>
        <div>
          <div className="cs-l">{t('engagementChart.engagement')}</div>
          <div className="cs-v">{headlineValue}{!isCountMode && headlineValue !== '—' && <span className="cs-unit">%</span>}</div>
        </div>
        <div className="cs-legend">
          <span className="cs-leg"><i style={{ background: 'var(--accent)' }} />{seriesLabel}</span>
          <span className="cs-leg"><i style={{ background: 'var(--text-subtle)', opacity: 0.6 }} />{t('engagementChart.postsRightAxis')}</span>
        </div>
      </div>
      <div className="chart-plot" ref={plotRef} onMouseLeave={onLeave}>
      <div className="chart-svg-wrap" tabIndex={0} role="region" aria-label={t('engagementChart.chartRegion')}>
        <div className="chart-canvas" onMouseMove={onMove}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="chart-svg" role="img" aria-label={`${seriesLabel}; ${t('engagementChart.postsRightAxis')}. ${t('socialMetrics.chartHint')}`}>
          <defs>
            <linearGradient id="engGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.20" />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="engStroke" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="var(--grad-cyan)" />
              <stop offset="50%" stopColor="var(--grad-purple)" />
              <stop offset="100%" stopColor="var(--grad-magenta)" />
            </linearGradient>
          </defs>

          {Array.from({ length: yLabels + 1 }).map((_, i) => {
            const y = padY + (i / yLabels) * innerH;
            const v = maxY - (i / yLabels) * (maxY - minY);
            return (
              <g key={i}>
                <line
                  x1={padX} x2={W - padX} y1={y} y2={y}
                  stroke="var(--border-subtle)" strokeWidth="1"
                  strokeDasharray={i === yLabels ? '0' : '2 3'}
                />
                <text
                  x={padX - 8} y={y + 3}
                  textAnchor="end" fontSize="12"
                  fill="var(--text-subtle)" fontFamily="var(--font-mono)"
                >
                  {selectedMetric === 'views' || isCountMode ? formatMetric(Math.round(v), i18n.resolvedLanguage || i18n.language || 'en') : `${v.toFixed(maxY < 10 ? 1 : 0)}%`}
                </text>
              </g>
            );
          })}

          <text x={W - padX + 7} y={padY + 3} textAnchor="start" fontSize="12" fill="var(--text-subtle)" fontFamily="var(--font-mono)">{maxPosts}</text>
          <text x={W - padX + 7} y={padY + innerH + 3} textAnchor="start" fontSize="12" fill="var(--text-subtle)" fontFamily="var(--font-mono)">0</text>

          {areaPath && <path d={areaPath} fill="url(#engGrad)" />}

          {publishedPath && <path d={publishedPath} fill="none" stroke="var(--text-subtle)" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.7" />}

          {gapBridges.map(([start, end], index) => <path key={index} d={`M ${start[0]} ${start[1]} L ${end[0]} ${end[1]}`} fill="none" stroke="var(--accent)" strokeWidth="3" strokeDasharray="3 5" opacity="0.95" />)}
          {selectedPath && (
            <path d={selectedPath} fill="none" stroke={selectedMetric === 'views' ? 'var(--accent)' : 'url(#engStroke)'} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          )}
          {slice.map((row, index) => metricNumber(row[selectedMetric]) !== null && <circle key={index} cx={xy(index, row[selectedMetric])[0]} cy={xy(index, row[selectedMetric])[1]} r="2.5" fill="var(--accent)" />)}

          {hover != null && slice[hover] && (() => {
            const [hx, hy] = xy(hover, metricNumber(slice[hover][selectedMetric]) ?? 0);
            const [px, py] = xyPosts(hover, metricNumber(slice[hover].published) ?? 0);
            return (
              <g>
                <line x1={hx} x2={hx} y1={padY} y2={padY + innerH} stroke="var(--accent)" strokeWidth="1" strokeDasharray="2 3" opacity="0.5" />
                {metricNumber(slice[hover].published) !== null && <circle cx={px} cy={py} r="3" fill="var(--bg-elev)" stroke="var(--text-subtle)" strokeWidth="1.4" />}
                {metricNumber(slice[hover][selectedMetric]) !== null && <circle cx={hx} cy={hy} r="4.5" fill="var(--bg-elev)" stroke="var(--accent)" strokeWidth="2" />}
              </g>
            );
          })()}

          {xLabels.map((i, k) => {
            const d = slice[i];
            const [x] = xy(i, 0);
            return (
              <text key={k} x={x} y={H - 4} textAnchor="middle" fontSize="12" fill="var(--text-subtle)" fontFamily="var(--font-mono)">
                {d?.label || ''}
              </text>
            );
          })}
        </svg>
        </div>
      </div>
        {hover != null && slice[hover] && (() => {
          return (
            <div className="chart-tip" style={{ left: tooltipLeft }}>
              <div className="chart-tip-l">{slice[hover].label}</div>
              <div className="chart-tip-row">
                <i style={{ background: 'var(--accent)' }} />
                <span>{seriesLabel}</span>
                <b className="mono">{formatValue(slice[hover][selectedMetric])}{metricNumber(slice[hover][selectedMetric]) !== null ? valueUnit : ''}</b>
              </div>
              <div className="chart-tip-row">
                <i style={{ background: 'var(--text-subtle)', opacity: 0.6 }} />
                <span>{t('engagementChart.published')}</span>
                <b className="mono">{metricNumber(slice[hover].published) ?? '—'}</b>
              </div>
            </div>
          );
        })()}
      </div>
      <details className="chart-data-details">
        <summary><I.info size={13} aria-hidden="true" />{t('engagementChart.dataDetails')}</summary>
        <div className="chart-data-details-body">
          {Number.isFinite(publishedTargets) && publishedTargets > 0 && <p>{t('engagementChart.viewsCoverage', { measured: viewsMeasuredTargets, published: publishedTargets })}</p>}
          {hasCoverage && <p>{t(valueMode === 'rate' ? 'analyticsPanel.rateCoverage' : 'analyticsPanel.interactionsCoverage', { measured: measuredTargets, published: publishedTargets })}</p>}
          {partial && <p>{t('analyticsPanel.partialCoverageHint')}</p>}
          {!isCountMode && <p>{t('engagementChart.rateFormula')}</p>}
          <p>{t('socialMetrics.chartHint')}</p>
          {gapBridges.length > 0 && <p>{t('engagementChart.gapBridgeHint')}</p>}
          <p className="chart-scroll-hint">{t('engagementChart.scrollHint')}</p>
        </div>
      </details>
      <MetricSeriesTable data={slice} fields={[{ key: 'views', label: t('socialMetrics.views') }, { key: 'published', label: t('engagementChart.published') }, { key: 'engagement', label: isCountMode ? t('engagementChart.engagement') : t('engagementChart.engagementPct') }]} label={t('analyticsPanel.overview')} />
    </div>
  );
}
